/**
 * 双选参考点主策略（互斥）。
 *
 * 设计说明：与 SolidWorks 测量对话框一致——点到点与圆弧/圆子模式互斥；
 * 进入测量时默认启用圆弧/圆测量（最小距离）；任一方为圆相关几何即可启用 ArcCircle 策略。
 */
export enum MeasurePairReferenceMode {
    /** 两几何体全局最近点（无独立工具栏入口，圆弧/圆策略不满足条件时降级使用）。 */
    ClosestPoints = 'closest-points',
    /** 两次拾取的世界坐标（点到点）。 */
    PointToPoint = 'point-to-point',
    /** 圆弧/圆专用参考点（任一方为圆弧/整圆边/圆曲面即可启用）。 */
    ArcCircle = 'arc-circle',
}

/**
 * 圆弧/圆子策略（仅 {@link MeasurePairReferenceMode.ArcCircle} 时生效）。
 */
export enum MeasureArcCircleReferenceMode {
    /** 圆心到圆心。 */
    CenterToCenter = 'center-to-center',
    /** 两圆（弧）之间的最小距离。 */
    MinimumDistance = 'minimum-distance',
    /** 两圆（弧）之间的最大距离。 */
    MaximumDistance = 'maximum-distance',
    /** 自定义距离（待交互参数，当前未实现）。 */
    CustomDistance = 'custom-distance',
}

/**
 * 测量会话内双选参考点策略快照。
 */
export interface MeasureReferencePolicy {
    /** 双选主策略。 */
    pairMode: MeasurePairReferenceMode;
    /** 圆弧/圆子策略。 */
    arcCircleMode: MeasureArcCircleReferenceMode;
}

/**
 * 创建进入测量时的默认参考点策略。
 *
 * @returns 默认策略（圆弧/圆测量 + 最小距离子模式）。
 */
export function createDefaultMeasureReferencePolicy(): MeasureReferencePolicy {
    return {
        pairMode: MeasurePairReferenceMode.ArcCircle,
        arcCircleMode: MeasureArcCircleReferenceMode.MinimumDistance,
    };
}

/**
 * 判断几何类别是否支持圆弧/圆测量参考点。
 *
 * @param command 工具栏命令键。
 * @returns 是否为圆弧/圆子命令。
 */
export function isMeasureArcCircleToolbarCommand(command: string): boolean {
    return (
        command === MeasureArcCircleReferenceMode.CenterToCenter
        || command === MeasureArcCircleReferenceMode.MinimumDistance
        || command === MeasureArcCircleReferenceMode.MaximumDistance
        || command === MeasureArcCircleReferenceMode.CustomDistance
        || command === 'arc-circle'
    );
}

/**
 * 将工具栏命令映射为圆弧/圆子策略；无法映射时返回 null。
 *
 * @param command 工具栏命令键。
 * @returns 子策略枚举；非圆弧/圆命令时返回 null。
 */
export function resolveArcCircleModeFromToolbarCommand(
    command: string,
): MeasureArcCircleReferenceMode | null {
    switch (command) {
        case MeasureArcCircleReferenceMode.CenterToCenter:
        case 'arc-circle':
            return MeasureArcCircleReferenceMode.CenterToCenter;
        case MeasureArcCircleReferenceMode.MinimumDistance:
            return MeasureArcCircleReferenceMode.MinimumDistance;
        case MeasureArcCircleReferenceMode.MaximumDistance:
            return MeasureArcCircleReferenceMode.MaximumDistance;
        case MeasureArcCircleReferenceMode.CustomDistance:
            return MeasureArcCircleReferenceMode.CustomDistance;
        default:
            return null;
    }
}
